/* eslint-disable no-constant-condition */
import { onReady } from "~/foundation/Events/onReady";
import { addEvent } from "~/foundation/Events/events";
import { addClass, removeClass } from "~/foundation/Dom/classList";
import { fetcher } from "~/foundation/Api";
import { appendElement, parseHTML, deleteElement } from "~/foundation/Dom/elementManipulation";
import stepOneContentTemplate from "./templates/step-one";
import stepTwoContentTemplate from "./templates/step-two";
import stepThreeContentTemplate from "./templates/step-three";
import stepFourContentTemplate from "./templates/step-four";
import stepFiveContentTemplate from "./templates/step-five";
import stepSixContentTemplate from "./templates/step-six";
import stepSevenContentTemplate from "./templates/step-seven";
import stepAdditionalContentTemplate from "./templates/additional.js";
import stepEightContentTemplate from "./templates/step-eight";
import { FormValidate } from "~/foundation/FormValidate";
import { onClickOutside, removeOnClickOutside } from "~/foundation/Events/onClickOutside";
import { forEach } from "~/foundation/Helpers/forEach";
import anime from "animejs";
import { STANDARDCUBICBEZIER } from "~/foundation/Styles/easings";
import { MEDIUM } from "~/foundation/Styles/durations";
import { addLoader } from "~/foundation/Loaders/loader";
import { scrollTo } from "~/foundation/Dom/scrollTo";
import { LocalDatetime } from "~/foundation/LocalDatetime/local-datetime";
import { setScrollLock, removeScrollLock } from "~/foundation/Helpers/extendedScrollLock";
import { isIOS } from "~/foundation/Helpers/isIOS.ts";

export class VolunteerSignup {
	/**
		 * Internal placeholder for cached DOM-objects.
		 *
		 * @type {object}
		 * @ignore
		 */
	dom = {
		container: undefined
	};

	classes = {
		rootClass: "volunteer-signup"
	};

	/**
		 *
		 * @param {Element} domReference - The element to work from.
		 */
	constructor(domReference) {
		this.dom.container = domReference;
		this.dom.eventPage = document.body.querySelector(".event-page");
		this.dom.signupContainer = document.body.querySelector(".volunteer-signup");

		this.dom.backgroundContainer = document.body.querySelector(".volunteer-signup__background");
		this.dom.signupForm = this.dom.signupContainer.querySelector(".volunteer-signup__form");
		this.dom.stepsFooter = this.dom.signupContainer.querySelector(".volunteer-signup__footer");
		this.dom.stepsContainer = this.dom.signupContainer.querySelector(".volunteer-signup__steps");
		this.dom.stepsIndicatorContainer = this.dom.signupContainer.querySelector(".volunteer-signup__steps-indicator");
		this.dom.closeButton = this.dom.signupContainer.querySelector(".volunteer-signup__close-button");
		this.dom.continueButton = this.dom.signupContainer.querySelector(".volunteer-signup__buttons--continue");
		this.dom.backButton = this.dom.signupContainer.querySelector(".volunteer-signup__buttons--back");

		this.classes = {
			...this.classes,
			openSignupClass: `${this.classes.rootClass}--open`,
			stepClass: `${this.classes.rootClass}__step`,
			activeClass: "event-page--show-signup",
			formInvalidField: "form-validate__field--invalid"
		};

		onReady(() => this.initialize());
	}

	getSectionsNotHidden = obj => {
		const result = {};

		for (const [sectionKey, section] of Object.entries(obj)) {
			if (section.hideSection === false) {
				result[sectionKey] = section;
			}
		}
		return result;
	};

	// Simple function to set the correct texts according to the response to keep the logic cleaner all around
	setupButtons = () => {
		this.dom.closeButton.innerText = this.signupDictionary.cancelButtonLabel;
		this.dom.continueButton.innerText = this.signupDictionary.continueButtonLabel;
		this.dom.backButton.innerText = this.signupDictionary.backButtonLabel;
		addClass(this.dom.backButton, `${this.classes.rootClass}__buttons--hidden`);
	};

	updateContinueButton = () => {
		// Always updating our "state" of the continue button aka the data attribute
		if (this.currentStep <= this.settings.totalSteps) {
			removeClass(this.dom.backButton, `${this.classes.rootClass}__buttons--hidden`);
		}

		if (this.signupDictionary) {
			if (this.currentStep === this.settings.totalSteps - 1) {
				this.dom.continueButton.innerText =
					this.signupDictionary.submitButtonLabel;
				removeClass(
					this.dom.backButton,
					`${this.classes.rootClass}__buttons--hidden`
				);
			}
			if (this.currentStep === 0) {
				addClass(
					this.dom.backButton,
					`${this.classes.rootClass}__buttons--hidden`
				);
			}
			if (this.currentStep === this.settings.totalSteps) {
				this.dom.continueButton.innerText =
					this.signupDictionary.closeButtonLabel;
				addClass(
					this.dom.backButton,
					`${this.classes.rootClass}__buttons--hidden`
				);
			}
			if (
				this.currentStep < this.settings.totalSteps - 1 &&
				this.dom.continueButton.innerText !==
				this.signupDictionary.continueButtonLabel
			) {
				this.dom.continueButton.innerText =
					this.signupDictionary.continueButtonLabel;
			}
		}
	};

	createStepIndicator = (stepNumber, currentStep) => {
		return `<div class="${this.classes.rootClass}__steps-indicator-step ${stepNumber === currentStep
			? `${this.classes.rootClass}__steps-indicator-step--active`
			: ""
			}"></div>`;
	};

	buildStepsIndicator = () => {
		const MAX_STEPS = this.settings.totalSteps;

		this.dom.stepsIndicatorContainer.innerHTML = [
			...Array(MAX_STEPS).keys()
		]
			.map(i => this.createStepIndicator(i, this.currentStep))
			.join("");
	};

	// BUILDER FUNCTIONS
	buildStepWrapper = (stepContent, stepNumber) => {
		return `
            <div class=${this.classes.stepClass} ${stepNumber > 1 ? `hidden` : ``
			}>
                ${stepContent}
            </div>`;
	};

	// Move out / create 'factory'-ish func if same structure is followed
	buildStepFromSections = (sections, stepTemplateId) => {
		const model = this.allSections;
		const classes = this.classes;
		let stepContent;

		// eslint-disable-next-line default-case
		switch (stepTemplateId) {
			case 1:
				stepContent = stepOneContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case 2:
				stepContent = stepTwoContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case 3:
				stepContent = stepThreeContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case 4:
				stepContent = stepFourContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case 5:
				stepContent = stepFiveContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case 6:
				stepContent = stepSixContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case 7:
				stepContent = stepSevenContentTemplate({
					classes,
					model,
					sections
				});
				break;
			case "additional":
				stepContent = stepAdditionalContentTemplate({
					classes,
					model,
					section: sections
				});
				break;
			case 8:
				stepContent = stepEightContentTemplate({
					classes,
					model,
					sections
				});
				break;
		}

		return this.buildStepWrapper(stepContent, stepTemplateId);
	};

	fetchData = eventId => {
		// Fetch the data
		fetcher(`${this.settings.endPoint}?eventId=${eventId}`).then(
			({ data }) => {
				this.signupFlowLoaded = true;
				// All the sections
				this.allSections = data;
				// Filter out the sections that is hidden (hideSection: true)
				this.shownSections = this.getSectionsNotHidden(data.formFieldSections);
				// Additional sections
				this.additionalSections = this.getSectionsNotHidden(data.additionalFormFieldSections);
				// Maybe we need this??
				this.stepLabels = Object.keys(
					this.shownSections
				);
				// The "dictionary" part of the response
				this.signupDictionary = data.texts;

				// Adding the event id in the create volunteer object
				this.volunteerData.eventId = eventId;

				this.settings.submitApiUrl = data.api.submitApiUrl;

				window.dataLayer = window.dataLayer || [];

				// STEP 1 -> USER INFO
				if (this.shownSections["section-details"]) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								this.shownSections[
									"section-details"
								],
								1
							)
						),
						this.dom.stepsContainer
					);

					this.dom.continueButton.focus();

					// Push event step to dataLayer
					window.dataLayer.push({
						event: "eventSignupFlow",
						eventId: this.settings.eventId,
						eventFunnelStep: 1
					});
				}

				// STEP 2
				if (
					this.shownSections["section-age"] &&
					this.shownSections["section-gender"]
				) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								[
									this.shownSections[
										"section-age"
									],
									this.shownSections[
										"section-gender"
									]
								],
								2
							)
						),
						this.dom.stepsContainer
					);
				}

				// STEP 3
				if (
					this.shownSections[
						"section-business-lines"
					]
				) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								this.shownSections[
									"section-business-lines"
								],
								3
							)
						),
						this.dom.stepsContainer
					);
				}

				// STEP 4
				if ((this.shownSections[
					"section-en-language-level"
				] &&
						this.shownSections[
							"section-ar-language-level"
						])
				) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								[
									this.shownSections[
										"section-en-language-level"
									],
									this.shownSections[
										"section-ar-language-level"
									]
								],
								4
							)
						),
						this.dom.stepsContainer
					);
				}

				// STEP 5
				if (
					this.shownSections["section-motivation"]
				) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								this.shownSections[
									"section-motivation"
								],
								5
							)
						),
						this.dom.stepsContainer
					);
				}

				// STEP 6
				if (this.shownSections["section-region"]) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								this.shownSections[
									"section-region"
								],
								6
							)
						),
						this.dom.stepsContainer
					);
				}

				// STEP 7
				if ((this.shownSections[
					"section-anything-to-add"
				] &&
						this.shownSections["section-username"])
				) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(
								[
									this.shownSections[
										"section-anything-to-add"
									],
									this.shownSections[
										"section-username"
									]
								],
								7
							)
						),
						this.dom.stepsContainer
					);
				}

				// Additional steps
				for (let index = 0; index < Object.keys(this.additionalSections).length; index++) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(Object.entries(this.additionalSections)[index][1], "additional")
						),
						this.dom.stepsContainer
					);
				}

				// STEP SIGNUP
				if (this.signupDictionary) {
					appendElement(
						parseHTML(
							this.buildStepFromSections(this.signupDictionary, 8)
						),
						this.dom.stepsContainer
					);
				}

				this.settings.totalSteps = this.dom.stepsContainer.children.length - 1;

				this.settings.stepWidth =
					this.dom.stepsContainer.getBoundingClientRect().width;
				this.settings.signupContainerWidth =
					this.dom.signupContainer.getBoundingClientRect().width;

				this.dom.stepCollection =
					this.dom.signupContainer.querySelectorAll(
						`.${this.classes.rootClass}__step`
					);

				this.dom.currentStepElement =
					this.dom.stepsContainer.children[0];

				this.buildStepsIndicator();
				this.setupButtons();

				anime({
					targets: [this.dom.stepsContainer, this.dom.stepsFooter],
					opacity: [0, 1],
					easing: STANDARDCUBICBEZIER,
					duration: MEDIUM
				});

				// Adding validation for form fields
				this.formValidate = new FormValidate(this.dom.signupContainer, {
					saveDataWhenOffline: false,
					beforeSubmit: () => {
						// Previous errors (if any) have been removed from all fields and validation is about to run.
						// If you have any code you wish to execute before validation, you can put it here.
					},
					onSubmit: fields => {
						window.console.log(fields);
					},
					errorMessageTargetClass: `${this.classes.rootClass}__error-message-target`
				});

				new LocalDatetime(
					this.dom.signupContainer.querySelector(
						'[data-async-module="local-datetime"]'
					)
				);
			}
		);
	};

	checkValidity = stepToMoveTo => {
		forEach(
			this.dom.currentStepElement.querySelectorAll("input[required]"),
			currentStepInput => {
				this.formValidate.checkFieldFromElement(currentStepInput);
			}
		);

		const invalidField = this.dom.currentStepElement.querySelector(
			`.${this.classes.formInvalidField}`
		);

		return new Promise((resolve, reject) => {
			if (!invalidField || this.currentStep > stepToMoveTo) {
				resolve("Success validating inputs");
			} else {
				reject(invalidField);
				// Inputs did not validate!
			}
		});
	};

	collectStepData = () => {
		const checkboxValues = [];
		const additionalCheckboxValues = {};

		for (const inputField of this.dom.currentStepElement.querySelectorAll("input, textarea")) {
			if (inputField.name.startsWith("additional.")) {
				const key = inputField.name.substring("additional.".length);
				if ((inputField.type === "radio" || inputField.type === "checkbox") && !inputField.checked) {
					continue
				}
				if (inputField.type === "checkbox" && inputField.checked) {
					additionalCheckboxValues[key] = additionalCheckboxValues[key] ?? [];
					additionalCheckboxValues[key].push(inputField.value);
					continue;
				}
				if (inputField.type === "textarea") {
					additionalCheckboxValues[key] = additionalCheckboxValues[key] ?? [];
					additionalCheckboxValues[key].push(inputField.value);
					continue;
				}
				this.additionalData.push(`${key}=${inputField.value}`);
				continue
			}
			if (inputField.type === "radio") {
				if (inputField.checked) {
					this.volunteerData[inputField.name] = inputField.value;
				}
			} else if (inputField.type === "checkbox") {
				if (inputField.checked) {
					checkboxValues.push(inputField.value);

					this.volunteerData[inputField.name] =
							checkboxValues.join(",");
				}
			} else {
				this.volunteerData[inputField.name] = inputField.value;
			}
		}
		Object.keys(additionalCheckboxValues).forEach((key) => {
			this.additionalData.push(`${key}=${additionalCheckboxValues[key].join(",")}`);
		})
	};

	applyForEvent = target => {
		this.checkValidity(this.currentStep + 1)
			.then(success => { // eslint-disable-line
				// Adding the loading state to the button
				addLoader(this.dom.signupForm, { replace: false });

				// disable the button
				this.dom.continueButton.disabled = true;

				// Storing data on each step
				this.collectStepData();

				this.volunteerData.additionalData = this.additionalData.join("|");

				fetcher(
					this.settings.submitApiUrl,
					"POST",
					this.volunteerData
				).then(data => {
					// Remove the loader
					const loader = this.dom.signupForm.querySelector(".loader");
					deleteElement(loader);

					if (data.success) {
						const isOnWaitingList = data.data?.waitingList;
						// Push final step to dataLayer
						window.dataLayer.push({
							event: isOnWaitingList ? "eventSignupWaitingListComplete" : "eventSignupComplete",
							eventId: this.settings.eventId
						});

						// enable the button
						this.dom.continueButton.disabled = false;

						deleteElement(
							this.dom.signupContainer.querySelector(
								`.${this.classes.rootClass}__steps-indicator`
							)
						);

						//Change confirmation text if participant ends on waitinglist before showing last step
						if (isOnWaitingList) {
							this.dom.stepsContainer.querySelector("[data-event-signup-confirmation-title]").innerText = this.signupDictionary.waitingListConfirmationTitle;
							this.dom.stepsContainer.querySelector("[data-event-signup-confirmation-text]").innerHTML = `<p>${this.signupDictionary.waitingListConfirmationText}</p>`;
						}

						this.moveSteps(this.currentStep + 1);

						const submitTheme = this.dom.signupContainer.parentNode.dataset.submitTheme;
						this.dom.signupContainer.classList.add(submitTheme);
					} else {
						// Find the error element
						const tempErrorElement =
							this.dom.currentStepElement.querySelectorAll(
								".form-validate__error-message"
							);

						if (tempErrorElement.length > 1) {
							forEach(tempErrorElement, element => {
								element.parentNode.removeChild(element);
							});
							appendElement(
								parseHTML(
									`<div class="form-validate__error-message ${data.errors.map(
										error => error.property
									)}">${data.errors.map(
										error => error.message
									)}</div>`
								),
								this.dom.currentStepElement.querySelector(
									"fieldset"
								)
							);
						} else if (tempErrorElement.length === 1) {
							forEach(tempErrorElement, element => {
								// Add error message
								element.innerText = data.errors.map(
									error => error.message
								);
								// Show error message
								element.style.display = "block";
								element.style.visibility = "visible";
							});
						} else {
							appendElement(
								parseHTML(
									`<div class="form-validate__error-message ${data.errors.map(
										error => error.property
									)}">${data.errors.map(
										error => error.message
									)}</div>`
								),
								this.dom.currentStepElement.querySelector(
									"fieldset"
								)
							);
						}

						// enable the button
						this.dom.continueButton.disabled = false;
					}
				});
			})
			.catch(invalidField => {
				void scrollTo(
					invalidField.parentNode?.getBoundingClientRect().top,
					200,
					this.dom.stepsContainer,
					100,
					false,
					true
				);
			});
	};

	moveSteps = stepToMoveTo => {
		// Checking if the fields of the current step are valid
		this.checkValidity(stepToMoveTo)
			.then(success => { // eslint-disable-line
				// Push event step to dataLayer
				window.dataLayer.push({
					event: "eventSignupFlow",
					eventId: this.settings.eventId,
					eventFunnelStep: stepToMoveTo
				});

				// Updating the current step
				this.currentStep = stepToMoveTo;

				// Scrolling content
				this.animateStepContent();

				// Updated steps indicator, move background and update the continue button.
				this.buildStepsIndicator(stepToMoveTo);
				this.updateContinueButton();

				// Storing data on each step
				this.collectStepData();

				// Updating currentStepElement
				this.dom.currentStepElement = this.dom.stepsContainer.children[this.currentStep];

				if (this.dom.currentStepElement?.querySelector('[data-async-module="local-datetime"]')) {
					new LocalDatetime(this.dom.currentStepElement?.querySelector('[data-async-module="local-datetime"]'));
				}
			})
			.catch(invalidField => {
				console.log("invalidField", invalidField);
				void scrollTo(
					invalidField.parentNode?.getBoundingClientRect().top,
					200,
					this.dom.stepsContainer,
					100,
					false,
					true
				);
			});
	};

	animateStepContent = () => {
		anime
			.timeline({
				easing: STANDARDCUBICBEZIER
			})
			.add({
				targets: this.dom.stepsContainer,
				opacity: [1, 0],
				easing: STANDARDCUBICBEZIER,
				duration: MEDIUM,
				complete: () => {
					forEach(this.dom.stepCollection, step => {
						step.setAttribute("hidden", true);
					});

					this.dom.currentStepElement.removeAttribute("hidden");
				}
			})
			.add({
				targets: this.dom.stepsContainer,
				opacity: [0, 1],
				easing: STANDARDCUBICBEZIER,
				duration: MEDIUM
			});
	};

	openSignup = () => {
		// For resetting purposes - REMOVE THIS PART IF WE WANT TO REMEMBER PROGRESS
		// if (this.currentStep !== 1) {
		//     this.currentStep = 1;

		//     this.moveSteps(this.currentStep);
		// }

		// enable the button
		this.dom.continueButton.disabled = false;

		if (this.dom.currentStepElement) {
			anime({
				targets: [this.dom.stepsContainer, this.dom.stepsFooter],
				opacity: [0, 1],
				easing: STANDARDCUBICBEZIER,
				duration: MEDIUM
			});
		}

		this.updateContinueButton();

		setScrollLock();

		addClass(this.dom.signupContainer, this.classes.openSignupClass);
		addClass(this.dom.eventPage, this.classes.activeClass);

		onClickOutside(this.dom.signupContainer, this.closeSignup, window, true);

		if (!this.signupFlowLoaded) {
			this.setupLazyEvents();
		}
	};

	closeSignup = () => {
		removeScrollLock();

		removeClass(this.dom.signupContainer, this.classes.openSignupClass);
		removeClass(this.dom.eventPage, this.classes.activeClass);

		removeOnClickOutside(this.dom.signupContainer, this.closeSignup, window);

		anime({
			targets: [this.dom.stepsContainer, this.dom.stepsFooter],
			opacity: [1, 0],
			easing: STANDARDCUBICBEZIER,
			duration: MEDIUM,
			delay: 200
		});
	};

	loadSignupOverlay = () => {
		// remove focus from apply button
		this.dom.container.blur();

		if (!this.signupFlowLoaded) {
			this.settings.endPoint && this.fetchData(this.settings.eventId);
		}
		this.openSignup();
	};

	setupLazyEvents = () => {
		// Prevent submitting the form when clicking ENTER when input fields has focus
		addEvent(this.dom.signupForm, "keypress", e => {
			const key = e.charCode || e.keyCode || 0;
			if (e.target.nodeName === "INPUT" && key === 13) {
				e.preventDefault();
			}
		});

		// Closing the signup on close button and cancel button click
		addEvent(this.dom.closeButton, "click", this.closeSignup);

		addEvent(this.dom.continueButton, "click", event => {
			if (
				this.currentStep <
				this.settings.totalSteps &&
				this.currentStep !==
				this.settings.totalSteps - 1
			) {
				this.moveSteps(this.currentStep + 1);
			} else if (
				this.currentStep ===
				this.settings.totalSteps - 1
			) {
				this.applyForEvent(event.target);
			} else if (
				this.currentStep ===
				this.settings.totalSteps
			) {
				this.closeSignup();

				const isApp = document.querySelector(".is-app") !== null;
				if (isApp && isIOS()){
					window.location.href = "aramcolife://reload";
				} else {
					window.location.href = window.location.origin + window.location.pathname + window.location.search;
				}
			}
		});

		addEvent(this.dom.backButton, "click", () => {
			if (
				this.currentStep > 0 &&
				this.currentStep < this.settings.totalSteps
			) {
				this.moveSteps(this.currentStep - 1);
			}
		});
	};

	initialize() {
		this.settings = {
			endPoint: this.dom.signupContainer.dataset.endpoint,
			eventId: this.dom.container.dataset.eventid.replace(/[{}]/g, ""),
			totalSteps: 0,
			stepWidth: null,
			signupContainerWidth: null,
		};

		this.volunteerData = {};
		this.additionalData = [];

		this.currentStep = 0;

		this.signupFlowLoaded = false;

		// Start signup flow
		addEvent(this.dom.container, "click", () => {
			this.loadSignupOverlay();
		});
	}
}
